/**
 * 플래너 스텝 추적 훅
 * 
 * 플래너 생성 프로세스의 각 단계를 추적
 * - planner_step_view: 스텝 진입
 * - planner_step_submit: 스텝 완료 (성공/실패)
 * 
 * ⚠️ intent_id는 내부적으로 planner_flow 의도의 intent_id를 세션 스토리지에서 자동으로 참조합니다.
 * 
 * @example
 * ```tsx
 * function PlannerStepPage() {
 *   const plannerStep = usePlannerStep();
 *   const [currentStep, setCurrentStep] = useState<PlannerStepKey>('q_spend_category');
 *   
 *   useEffect(() => {
 *     // 스텝 진입 (intent_id는 planner_flow에서 자동으로 가져옴)
 *     plannerStep.view({
 *       step_key: currentStep,
 *       step_index: 1,
 *     });
 *   }, [currentStep]);
 *   
 *   const handleSubmitStep = async (data: any) => {
 *     try {
 *       await processStep(currentStep, data);
 *       
 *       // 스텝 완료 (성공)
 *       plannerStep.submit({
 *         step_key: currentStep,
 *         step_index: 1,
 *         outcome: 'ok',
 *       });
 *       
 *       setCurrentStep(getNextStep(currentStep));
 *     } catch (error) {
 *       // 스텝 완료 (실패)
 *       plannerStep.submit({
 *         step_key: currentStep,
 *         step_index: 1,
 *         outcome: 'fail',
 *         fail_reason: 'validation_error',
 *       });
 *     }
 *   };
 * }
 * ```
 */

import { useCallback } from 'react';
import { ulid } from 'ulid';
import { track } from '../../../../core/client';
import { getIntentId } from '../../../../core/ids';
import type { PlannerStepKey } from '../../../../core/schema';

export interface PlannerStepViewParams {
    step_key: PlannerStepKey;
    step_index: number;
}

export interface PlannerStepSubmitParams {
    step_key: PlannerStepKey;
    step_index: number;
    outcome?: 'ok' | 'fail';
    fail_reason?: string;
}

export interface PlannerStepActions {
    view: (params: PlannerStepViewParams) => void;
    submit: (params: PlannerStepSubmitParams) => void;
}

/**
 * 플래너 스텝 추적 훅
 */
export function usePlannerStep(): PlannerStepActions {
    /**
     * planner_flow 의도의 intent_id를 세션 스토리지에서 가져오기
     * 없으면 fallback으로 새로 생성
     */
    const getPlannerFlowIntentId = useCallback((): string => {
        const plannerFlowIntent = getIntentId('planner_flow');
        if (plannerFlowIntent?.intentId) {
            return plannerFlowIntent.intentId;
        }
        // fallback: planner_flow가 없으면 새로 생성 (정상적인 flow에서는 발생하지 않아야 함)
        console.warn('[PlannerStep] planner_flow intent_id not found in session storage, generating new one');
        return ulid();
    }, []);

    /**
     * 스텝 진입 추적
     */
    const view = useCallback((params: PlannerStepViewParams) => {
        const intentId = getPlannerFlowIntentId();

        track('planner_step_view', {
            intent_id: intentId,
            step_key: params.step_key,
            step_index: params.step_index,
        });

    }, [getPlannerFlowIntentId]);

    /**
     * 스텝 완료 추적
     */
    const submit = useCallback((params: PlannerStepSubmitParams) => {
        const intentId = getPlannerFlowIntentId();

        track('planner_step_submit', {
            intent_id: intentId,
            step_key: params.step_key,
            step_index: params.step_index,
            outcome: params.outcome,
            fail_reason: params.fail_reason,
        });

    }, [getPlannerFlowIntentId]);

    return {
        view,
        submit,
    };
}

export type { PlannerStepKey };

